import { Identifier, XYCoord } from 'dnd-core';
import React, { MutableRefObject, useRef } from 'react';
import { useDrag, useDrop } from 'react-dnd';

import {
  StateControllerOptionForm,
  StateControllerOptionFormProps,
} from './StateControllerOptionForm';

interface StateControllerOptionFormDndWrapper {
  onMove: (dragIndex: number, hoverIndex: number) => void;
}

export function StateControllerOptionFormDndWrapper({
  index,
  onMove,
  ...props
}: Omit<StateControllerOptionFormProps, 'handlerId' | 'innerRef'> &
  StateControllerOptionFormDndWrapper) {
  const ref = useRef<HTMLDivElement>(null);
  const [{ handlerId }, drop] = useDrop<
    { index: number; id: string },
    void,
    { handlerId: Identifier | null }
  >({
    accept: 'option-field',
    collect(monitor) {
      return {
        handlerId: monitor.getHandlerId(),
      };
    },
    hover(item, monitor) {
      if (!ref.current) {
        return;
      }

      const dragIndex = item.index;
      const hoverIndex = index;

      // Don't replace items with themselves
      if (dragIndex === hoverIndex) {
        return;
      }

      // Determine rectangle on screen
      const hoverBoundingRect = ref.current?.getBoundingClientRect();

      // Get vertical middle
      const hoverMiddleY =
        (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;

      // Determine mouse position
      const clientOffset = monitor.getClientOffset();

      // Get pixels to the top
      const hoverClientY = (clientOffset as XYCoord).y - hoverBoundingRect.top;

      // Only perform the move when the mouse has crossed half of the items height
      // When dragging downwards, only move when the cursor is below 50%
      // When dragging upwards, only move when the cursor is above 50%

      // Dragging downwards
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }

      // Dragging upwards
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }

      onMove(dragIndex, hoverIndex);

      // Note: we're mutating the monitor item here!
      // Generally it's better to avoid mutations,
      // but it's good here for the sake of performance
      // to avoid expensive index searches.
      item.index = hoverIndex;
    },
  });

  const [, drag] = useDrag({
    type: 'option-field',
    item: () => ({ index, id: props.option.value }),
  });

  drag(drop(ref));

  return (
    <div style={{ cursor: 'grab' }}>
      <StateControllerOptionForm
        {...props}
        index={index}
        handlerId={handlerId}
        innerRef={ref as MutableRefObject<HTMLDivElement>}
      />
    </div>
  );
}
